import type {
  NoticeRegistry,
  RegisteredNotice,
  RegisteredNoticeKey,
} from 'components/DismissibleNotices/types';
import { NoticeTypeEnum } from 'components/DismissibleNotices/types';
import messages from './noticeTextContent';
import sampleReleaseNotesImage from './images/sample-release-notes.webp';

/**
 * Controls which notices can be muted/disabled from user settings or the notice footer.
 */
const USER_MUTABLE_NOTICES: NoticeTypeEnum[] = [NoticeTypeEnum.TIPS_AND_TRICKS];

/**
 * This is exposed through the `getNoticeRegistry` function to prevent direct access to the registry
 * and help test setup.
 */
const dismissibleNoticeRegistry: NoticeRegistry = {
  c2BoundaryResize: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  cardLibraryDataFieldChosen: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  columnsDropdown: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  dataTableInlineEditingWarning: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  dummyOnScreenNotice: {
    isDebugNotice: true,
    message: messages.dummyArtifactNotice,
    noticeType: NoticeTypeEnum.ON_SCREEN,
    screen: 'c2',
    title: messages.dummyArtifactNoticeTitle,
  },
  dummyReleaseNotesNotice: {
    image: sampleReleaseNotesImage,
    isDebugNotice: true,
    link: '/',
    description: messages['releaseNotes-1.0.0-description'],
    noticeType: NoticeTypeEnum.RELEASE_NOTES,
    sections: [
      {
        header: messages['releaseNotes-1.0.0-section1-header'],
        items: [
          messages['releaseNotes-1.0.0-section1-item1'],
          messages['releaseNotes-1.0.0-section1-item2'],
          messages['releaseNotes-1.0.0-section1-item3'],
        ],
      },
      {
        header: messages['releaseNotes-1.0.0-section2-header'],
        items: [
          messages['releaseNotes-1.0.0-section2-item1'],
          messages['releaseNotes-1.0.0-section2-item2'],
        ],
      },
    ],
    version: '1.0.0',
  },
  dummyTipsAndTricksNotice: {
    isDebugNotice: true,
    message: messages.dummyTipsAndTricksNotice,
    noticeType: NoticeTypeEnum.TIPS_AND_TRICKS,
  },
  favoritePlansForEasyAccess: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  fitToViewBannerDismissed: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  framesBannerDismissed: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  mapClippingOnboarding: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  planDashboardWelcomeBanner: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  userNotifiedOfFileViewSwitchClick: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  userNotifiedOfNoOneClick: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  migratedFinalLists: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  planSnapshots: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  aiAssistButton: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  aiAssistSidePaneSplashScreen: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  knowledgeBaseNotice: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  presenterView: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  templateBuilderImprovements: {
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  versionHistory: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  cardLibrarySearchOptions: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  commenterRole: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
  viewMenuCommentMode: {
    alwaysShow: true,
    noticeType: NoticeTypeEnum.MANUAL_TRIGGER,
  },
};

/**
 * Get the registered dismissible notices.
 */
const getNoticeRegistry = (): NoticeRegistry => {
  return dismissibleNoticeRegistry;
};

/**
 * Get all notices of a specific type.
 * @param type The type of notice to get.
 */
const getNoticesOfType = (type: NoticeTypeEnum): [RegisteredNoticeKey, RegisteredNotice][] => {
  const notices = Object.entries(dismissibleNoticeRegistry);
  return notices.filter(([_, notice]) => notice.noticeType === type) as [
    RegisteredNoticeKey,
    RegisteredNotice
  ][];
};

export { getNoticeRegistry, getNoticesOfType, USER_MUTABLE_NOTICES };
